import { DatabaseEvent, EventResponse, EventsResponse } from '../types/types';
import api from './config';

const EVENT_API_URL = `${process.env.REACT_APP_SERVER_URL}/event`;

/**
 * Fetches all events associated with a given user.
 *
 * @param username The username whose events should be retrieved.
 * @returns A promise resolving to an EventsResponse object.
 */
export const fetchEvents = async (username: string): Promise<EventsResponse> => {
  const response = await api.get<EventsResponse>(`${EVENT_API_URL}/getEvents`, {
    params: { username },
  });
  return response.data;
};

/**
 * Creates a new event using provided partial event data.
 *
 * @param eventData Partial event object containing the necessary fields to create an event.
 * @returns A promise resolving to an EventResponse object.
 */
export const createEvent = async (eventData: Partial<DatabaseEvent>): Promise<EventResponse> => {
  try {
    const response = await api.post<EventResponse>(`${EVENT_API_URL}/createEvent`, eventData);
    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error creating event:', error);
    throw error;
  }
};

/**
 * Deletes an existing event by its ID.
 *
 * @param eventId The unique ID of the event to delete.
 * @returns A promise resolving to an EventResponse object.
 */
export const deleteEvent = async (eventId: string): Promise<EventResponse> => {
  try {
    const response = await api.delete<EventResponse>(`${EVENT_API_URL}/deleteEvent/${eventId}`);
    return response.data;
  } catch (error) {
    return { error: 'Error deleting event' };
  }
};

/**
 * Updates an existing event with new data.
 *
 * @param eventId The ID of the event to update.
 * @param eventData Partial event data to update.
 * @returns A promise resolving to the updated DatabaseEvent.
 */
export const updateEvent = async (
  eventId: string,
  eventData: Partial<DatabaseEvent>,
): Promise<DatabaseEvent> => {
  const response = await api.patch(`${EVENT_API_URL}/updateEvent/${eventId}`, eventData);
  return response.data;
};

/**
 * Shares an event with another user.
 *
 * @param eventId The ID of the event to share.
 * @param username The username of the user to share the event with.
 * @returns A promise resolving to an EventResponse object.
 */
export const shareEvent = async (eventId: string, username: string): Promise<EventResponse> => {
  try {
    const response = await api.post(`${EVENT_API_URL}/shareEvent/${eventId}`, { username });
    return response.data;
  } catch (error) {
    return { error: 'Error sharing event' };
  }
};
